import { ActivatedRouteSnapshot, CanActivateChildFn, CanActivateFn, RouterStateSnapshot } from "@angular/router";
import { EnvironmentInjector, inject, runInInjectionContext } from "@angular/core";
import { concat, first, from, isObservable, last, Observable, of, takeWhile } from "rxjs";

export function isPromise<T = any>(obj: any): obj is Promise<T> {
    // allow any Promise/A+ compliant thenable.
    // It's up to the caller to ensure that obj.then conforms to the spec
    return !!obj && typeof obj.then === "function";
}

export function wrapIntoObservable<T>(value: T | Promise<T> | Observable<T>): Observable<T> {
    if (isObservable(value)) {
        return value;
    }

    if (isPromise(value)) {
        return from(Promise.resolve(value));
    }

    return of(value);
}

export function runSerially(guards: CanActivateFn[] | CanActivateChildFn[]): CanActivateFn | CanActivateChildFn {
    return (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
        const injector = inject(EnvironmentInjector);
        const observables = guards.map((guard) => {
            const guardResult = runInInjectionContext(injector, () => guard(route, state));
            return wrapIntoObservable(guardResult).pipe(first());
        });

        return concat(...observables).pipe(
            takeWhile((result) => result === true, true),
            last()
        );
    };
}
